import { useMemo } from 'react';
import { Alley } from '../../../../../services/entities/impl/blfApi/schemas/alley';
import { JsonLabel } from '../../../components/DebugInfoSection';

/**
 * Hook that creates memoized label objects for Alley data
 * Primary label is the alley name property
 *
 * @param alleys - Array of alley objects
 * @returns Memoized array of JsonLabel objects with id and formatted label
 */
export const useJsonLabelsAlleys = (
  alleys: Alley[] | undefined
): JsonLabel[] => {
  return useMemo(() => {
    if (!alleys) {
      return [];
    }

    // Map alleys to JsonLabel objects
    return alleys
      .filter((alley) => !!alley.id) // Filter out items without ID
      .map((alley) => ({
        id: alley.id,
        label: formatAlleyLabel(alley),
      }));
  }, [alleys]);
};

/**
 * Formats an alley label with the most relevant information
 */
const formatAlleyLabel = (alley: Alley): string => {
  const parts = [];

  // Primary information: alley name
  if (alley.alley) {
    parts.push(alley.alley);
  }

  // Add status indicators
  const flags = [];
  if (alley.isPassive) {
    flags.push('Passive');
  } else {
    flags.push('Labeled');
  }

  if (alley.junk) flags.push('Junk');

  if (flags.length > 0) {
    parts.push(`[${flags.join(', ')}]`);
  }

  return parts.join(' ');
};
